import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Spin,
  message,
  Button,
  DatePicker,
  Select,
  Space,
  Tag,
  Progress,
  List,
  Avatar,
  Empty,
  Modal,
  Switch
} from 'antd';
import {
  ShopOutlined,
  GiftOutlined,
  DollarOutlined,
  TrophyOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  UserOutlined,
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
  SettingOutlined,
  UpOutlined,
  DownOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { stationOwnerAPI } from '../services/api';
import {
  loadWidgetConfig,
  saveWidgetConfig,
  toggleWidgetVisibility,
  moveWidget,
  resetWidgetConfig,
  WidgetConfig
} from '../utils/widgetConfig';
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface DashboardStats {
  totalStations: number;
  activeStations: number;
  verifiedStations: number;
  totalCampaigns: number;
  activeCampaigns: number;
  totalRevenue: number;
  monthlyRevenue: number;
  lastMonthRevenue: number;
  yearlyRevenue: number;
  revenueTrend: number;
  totalVisits: number;
  totalRatings: number;
  averageRating: number;
  recentStations: any[];
  topPerformingStations: any[];
  revenueByDay: Array<{ date: string; revenue: number; transactions: number }>;
  campaignPerformance: Array<{ status: string; count: number; totalUses: number; totalViews: number }>;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<'7days' | '30days' | '90days' | 'custom'>('30days');
  const [widgetConfig, setWidgetConfig] = useState<WidgetConfig[]>(loadWidgetConfig());
  const [settingsModalVisible, setSettingsModalVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await stationOwnerAPI.getDashboard();
      setStats(response);
    } catch (error) {
      message.error('Dashboard verileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleWidget = (widgetId: string) => {
    const updated = toggleWidgetVisibility(widgetId, widgetConfig);
    setWidgetConfig(updated);
    saveWidgetConfig(updated);
    message.success('Widget ayarı güncellendi');
  };

  const handleMoveWidget = (widgetId: string, direction: 'up' | 'down') => {
    const updated = moveWidget(widgetId, direction, widgetConfig);
    setWidgetConfig(updated);
    saveWidgetConfig(updated);
  };

  const handleResetWidgets = () => {
    const reset = resetWidgetConfig();
    setWidgetConfig(reset);
    message.success('Widget ayarları sıfırlandı');
  };

  const getVisibleWidgets = () => {
    return widgetConfig.filter(w => w.visible).sort((a, b) => a.order - b.order);
  };

  const renderWidget = (widget: WidgetConfig) => {
    if (!widget.visible) return null;

    switch (widget.id) {
      case 'totalStations':
        return (
          <Col xs={24} sm={12} lg={6} key={widget.id}>
            <Card hoverable onClick={() => navigate('/panel/stations')} style={{ cursor: 'pointer', height: '100%' }}>
              <Statistic
                title="Toplam İstasyon"
                value={stats?.totalStations || 0}
                prefix={<ShopOutlined style={{ color: '#1890ff' }} />}
                valueStyle={{ color: '#1890ff' }}
                suffix={
                  <Tag color="success" style={{ marginLeft: 8 }}>
                    {stats?.activeStations || 0} Aktif
                  </Tag>
                }
              />
              <Progress
                percent={stats?.totalStations ? Math.round((stats.activeStations / stats.totalStations) * 100) : 0}
                size="small"
                strokeColor="#1890ff"
                style={{ marginTop: 8 }}
              />
            </Card>
          </Col>
        );
      case 'totalCampaigns':
        return (
          <Col xs={24} sm={12} lg={6} key={widget.id}>
            <Card hoverable onClick={() => navigate('/panel/campaigns')} style={{ cursor: 'pointer', height: '100%' }}>
              <Statistic
                title="Toplam Kampanya"
                value={stats?.totalCampaigns || 0}
                prefix={<GiftOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ color: '#52c41a' }}
                suffix={
                  <Tag color="success" style={{ marginLeft: 8 }}>
                    {stats?.activeCampaigns || 0} Aktif
                  </Tag>
                }
              />
              <Progress
                percent={stats?.totalCampaigns ? Math.round((stats.activeCampaigns / stats.totalCampaigns) * 100) : 0}
                size="small"
                strokeColor="#52c41a"
                style={{ marginTop: 8 }}
              />
            </Card>
          </Col>
        );
      case 'monthlyRevenue':
        return (
          <Col xs={24} sm={12} lg={6} key={widget.id}>
            <Card hoverable style={{ height: '100%' }}>
              <Statistic
                title="Aylık Gelir"
                value={stats?.monthlyRevenue || 0}
                prefix={<DollarOutlined style={{ color: '#f5222d' }} />}
                suffix="₺"
                valueStyle={{ color: '#f5222d' }}
                precision={2}
              />
              <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Geçen Ay: {stats?.lastMonthRevenue?.toLocaleString('tr-TR') || 0} ₺
                </Text>
                <div style={{ marginTop: 4 }}>
                  <Tag color={revenueChangeColor} icon={revenueChangeIcon}>
                    {stats?.revenueTrend ? Math.abs(stats.revenueTrend).toFixed(1) : 0}%
                  </Tag>
                </div>
              </div>
            </Card>
          </Col>
        );
      case 'averageRating':
        return (
          <Col xs={24} sm={12} lg={6} key={widget.id}>
            <Card hoverable style={{ height: '100%' }}>
              <Statistic
                title="Ortalama Değerlendirme"
                value={stats?.averageRating || 0}
                prefix={<TrophyOutlined style={{ color: '#faad14' }} />}
                valueStyle={{ color: '#faad14' }}
                precision={1}
                suffix="/ 5.0"
              />
              <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {stats?.totalRatings || 0} değerlendirme
                </Text>
                <Progress
                  percent={stats?.averageRating ? Math.round((stats.averageRating / 5) * 100) : 0}
                  size="small"
                  strokeColor="#faad14"
                  style={{ marginTop: 4 }}
                />
              </div>
            </Card>
          </Col>
        );
      case 'revenueChart':
        return (
          <Col xs={24} lg={16} key={widget.id}>
            <Card
              title={
                <Space>
                  <LineChartOutlined />
                  <span>Gelir Trendi (Son 7 Gün)</span>
                </Space>
              }
              extra={
                <Button type="link" onClick={() => navigate('/panel/analytics')}>
                  Detaylı Analiz
                </Button>
              }
            >
              {stats?.revenueByDay && stats.revenueByDay.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={stats.revenueByDay}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1890ff" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#1890ff" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip formatter={(value: any) => [`${value.toLocaleString('tr-TR')} ₺`, 'Gelir']} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      stroke="#1890ff"
                      fillOpacity={1}
                      fill="url(#colorRevenue)"
                      name="Gelir (₺)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Empty description="Henüz veri yok" />
              )}
            </Card>
          </Col>
        );
      case 'campaignPie':
        return (
          <Col xs={24} lg={8} key={widget.id}>
            <Card
              title={
                <Space>
                  <PieChartOutlined />
                  <span>Kampanya Durumu</span>
                </Space>
              }
            >
              {stats?.campaignPerformance && stats.campaignPerformance.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={stats.campaignPerformance}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }: any) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {stats.campaignPerformance.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Empty description="Henüz kampanya yok" />
              )}
            </Card>
          </Col>
        );
      case 'topStations':
        return (
          <Col xs={24} lg={12} key={widget.id}>
            <Card
              title={
                <Space>
                  <TrophyOutlined />
                  <span>En İyi Performans Gösteren İstasyonlar</span>
                </Space>
              }
              extra={
                <Button type="link" onClick={() => navigate('/panel/stations')}>
                  Tümünü Gör
                </Button>
              }
            >
              {stats?.topPerformingStations && stats.topPerformingStations.length > 0 ? (
                <List
                  dataSource={stats.topPerformingStations}
                  renderItem={(station: any, index: number) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={
                          <Avatar
                            style={{
                              backgroundColor: index === 0 ? '#ffd700' : index === 1 ? '#c0c0c0' : '#cd7f32'
                            }}
                          >
                            {index + 1}
                          </Avatar>
                        }
                        title={station.name}
                        description={
                          <Space>
                            <Text type="secondary">
                              <UserOutlined /> {station.totalVisits || 0} ziyaret
                            </Text>
                            <Text type="secondary">
                              <TrophyOutlined /> {station.rating?.toFixed(1) || 0}/5.0
                            </Text>
                          </Space>
                        }
                      />
                      <Tag color="success">{station.totalVisits || 0} ziyaret</Tag>
                    </List.Item>
                  )}
                />
              ) : (
                <Empty description="Henüz istasyon yok" />
              )}
            </Card>
          </Col>
        );
      case 'quickActions':
        return (
          <Col xs={24} lg={12} key={widget.id}>
            <Card title="Hızlı İşlemler">
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Button
                  type="default"
                  size="large"
                  icon={<ShopOutlined />}
                  onClick={() => navigate('/panel/stations')}
                  block
                  style={{
                    height: 'auto',
                    padding: '16px',
                    background: '#f6ffed',
                    borderColor: '#b7eb8f',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <span>
                    <ShopOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
                    Yeni İstasyon Ekle
                  </span>
                  <ArrowUpOutlined style={{ color: '#52c41a' }} />
                </Button>

                <Button
                  type="default"
                  size="large"
                  icon={<GiftOutlined />}
                  onClick={() => navigate('/panel/campaigns')}
                  block
                  style={{
                    height: 'auto',
                    padding: '16px',
                    background: '#fff7e6',
                    borderColor: '#ffd591',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <span>
                    <GiftOutlined style={{ color: '#faad14', marginRight: '8px' }} />
                    Yeni Kampanya Oluştur
                  </span>
                  <ArrowUpOutlined style={{ color: '#faad14' }} />
                </Button>

                <Button
                  type="default"
                  size="large"
                  icon={<BarChartOutlined />}
                  onClick={() => navigate('/panel/analytics')}
                  block
                  style={{
                    height: 'auto',
                    padding: '16px',
                    background: '#e6f7ff',
                    borderColor: '#91d5ff',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <span>
                    <BarChartOutlined style={{ color: '#1890ff', marginRight: '8px' }} />
                    Detaylı Analiz ve Raporlar
                  </span>
                  <ArrowUpOutlined style={{ color: '#1890ff' }} />
                </Button>

                <Button
                  type="default"
                  size="large"
                  icon={<UserOutlined />}
                  onClick={() => navigate('/panel/subusers')}
                  block
                  style={{
                    height: 'auto',
                    padding: '16px',
                    background: '#f9f0ff',
                    borderColor: '#d3adf7',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <span>
                    <UserOutlined style={{ color: '#722ed1', marginRight: '8px' }} />
                    Alt Kullanıcı Yönetimi
                  </span>
                  <ArrowUpOutlined style={{ color: '#722ed1' }} />
                </Button>
              </Space>
            </Card>
          </Col>
        );
      case 'yearlyRevenue':
        return (
          <Col xs={24} sm={12} lg={6} key={widget.id}>
            <Card>
              <Statistic
                title="Yıllık Gelir"
                value={stats?.yearlyRevenue || 0}
                prefix={<DollarOutlined />}
                suffix="₺"
                precision={2}
              />
            </Card>
          </Col>
        );
      case 'totalVisits':
        return (
          <Col xs={24} sm={12} lg={6} key={widget.id}>
            <Card>
              <Statistic
                title="Toplam Ziyaret"
                value={stats?.totalVisits || 0}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>
        );
      case 'totalRatings':
        return (
          <Col xs={24} sm={12} lg={6} key={widget.id}>
            <Card>
              <Statistic
                title="Toplam Değerlendirme"
                value={stats?.totalRatings || 0}
                prefix={<TrophyOutlined />}
              />
            </Card>
          </Col>
        );
      case 'verifiedStations':
        return (
          <Col xs={24} sm={12} lg={6} key={widget.id}>
            <Card>
              <Statistic
                title="Doğrulanmış İstasyon"
                value={stats?.verifiedStations || 0}
                prefix={<ShopOutlined />}
                suffix={`/ ${stats?.totalStations || 0}`}
              />
            </Card>
          </Col>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  const revenueChangeColor = stats?.revenueTrend && stats.revenueTrend >= 0 ? '#52c41a' : '#ff4d4f';
  const revenueChangeIcon = stats?.revenueTrend && stats.revenueTrend >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />;

  return (
    <div>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }} gutter={[16, 16]}>
        <Col xs={24} sm={24} md={12}>
          <Title level={2} style={{ margin: 0, color: '#333', fontSize: '24px' }}>
            Dashboard
          </Title>
          <Text type="secondary" style={{ display: 'block', marginTop: '4px' }}>
            İşletmenizin genel görünümü ve performans metrikleri
          </Text>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Space wrap style={{ width: '100%' }}>
              <Select
                value={dateRange}
                onChange={(value) => setDateRange(value)}
                style={{ width: '100%', maxWidth: 150 }}
              >
                <Option value="7days">Son 7 Gün</Option>
                <Option value="30days">Son 30 Gün</Option>
                <Option value="90days">Son 90 Gün</Option>
                <Option value="custom">Özel Tarih</Option>
              </Select>
              {dateRange === 'custom' && (
                <RangePicker style={{ width: '100%', maxWidth: 250 }} />
              )}
              <Button type="primary" icon={<BarChartOutlined />} onClick={fetchDashboardStats}>
                Yenile
              </Button>
              <Button icon={<SettingOutlined />} onClick={() => setSettingsModalVisible(true)}>
                Widget Ayarları
              </Button>
            </Space>
          </Space>
        </Col>
      </Row>

      {/* Widget'ları Dinamik Olarak Render Et */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {getVisibleWidgets()
          .filter(w => ['totalStations', 'totalCampaigns', 'monthlyRevenue', 'averageRating'].includes(w.id))
          .map(widget => renderWidget(widget))}
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {getVisibleWidgets()
          .filter(w => ['revenueChart', 'campaignPie'].includes(w.id))
          .map(widget => renderWidget(widget))}
      </Row>

      <Row gutter={[16, 16]}>
        {getVisibleWidgets()
          .filter(w => ['topStations', 'quickActions'].includes(w.id))
          .map(widget => renderWidget(widget))}
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        {getVisibleWidgets()
          .filter(w => ['yearlyRevenue', 'totalVisits', 'totalRatings', 'verifiedStations'].includes(w.id))
          .map(widget => renderWidget(widget))}
      </Row>

      {/* Widget Ayarları Modal */}
      <Modal
        title="Widget Ayarları"
        open={settingsModalVisible}
        onCancel={() => setSettingsModalVisible(false)}
        footer={[
          <Button key="reset" onClick={handleResetWidgets}>
            Varsayılanlara Sıfırla
          </Button>,
          <Button key="close" type="primary" onClick={() => setSettingsModalVisible(false)}>
            Kapat
          </Button>
        ]}
        width={600}
      >
        <List
          dataSource={widgetConfig.sort((a, b) => a.order - b.order)}
          renderItem={(widget, index) => (
            <List.Item
              actions={[
                <Button
                  key="up"
                  type="text"
                  icon={<UpOutlined />}
                  disabled={index === 0}
                  onClick={() => handleMoveWidget(widget.id, 'up')}
                />,
                <Button
                  key="down"
                  type="text"
                  icon={<DownOutlined />}
                  disabled={index === widgetConfig.length - 1}
                  onClick={() => handleMoveWidget(widget.id, 'down')}
                />,
                <Switch
                  key="toggle"
                  checked={widget.visible}
                  onChange={() => handleToggleWidget(widget.id)}
                />
              ]}
            >
              <List.Item.Meta
                title={widget.title}
                description={`Tip: ${widget.type === 'statistic' ? 'İstatistik' : widget.type === 'chart' ? 'Grafik' : widget.type === 'list' ? 'Liste' : 'İşlem'} | Boyut: ${widget.size === 'small' ? 'Küçük' : widget.size === 'medium' ? 'Orta' : 'Büyük'}`}
              />
            </List.Item>
          )}
        />
      </Modal>
    </div>
  );
};

export default Dashboard;
